<template>
  <div class="project">

    <nav class="navbar is-mobile is-medium" style="background-color: #30008A;" role="navigation" aria-label="main navigation">
      <div class="navbar-brand">
          <img id="navbar-logo" src="../../assets/projects/pp19/paaspop19.jpg" alt="Logo">
      </div>
    </nav>
  <section>
    <h1> Paaspop Red & Blue Trash Concept</h1>

    <!-- Project Information -->
    
      <p>
        <strong>Project Type:</strong> Interactive Festival Cleanup Solution
      </p>

    </section>

    <!-- Project Description -->
    <section>
      <h2>Project Description</h2>
      <p>
        The "Paaspop Red & Blue Trash Concept" was an innovative interactive solution designed for music festivals to encourage festivalgoers to actively participate in maintaining cleanliness. The project aimed to gamify the cleanup process and create an engaging experience for festival attendees. Here's an overview of the project:
      </p>
      <!-- Concept -->
      <h3>Concept</h3>
      <p>
        At music festivals, trash accumulation is a significant environmental concern. To address this issue, we introduced a unique concept that divided festivalgoers into two teams: Team Red and Team Blue. The festival grounds featured specially designed trash bins colored in red and blue. When attendees disposed of their trash in either a red or blue bin, it translated into points for the respective team. These points were displayed on screens throughout the festival grounds.
      </p>
      <p>
        To entice festivalgoers to choose their teams, we incorporated interactive questions alongside the real-time score updates. Questions such as "Pepsi or Cola?" were displayed on screens, with Team Red and Team Blue associated with specific choices. This approach not only educated participants about their choices but also added an element of fun to the voting process.
      </p>
      <!-- Hardware and Components -->
      <h3>Hardware and Components</h3>
      <p>
        The success of this project hinged on a range of hardware components, including sensors, Arduino Uno, ESP8266, specially designed trash bins, pipes, and more. These sensors were strategically placed within the bins to detect incoming trash. We opted for the ESP8266 for its wide signal radius of approximately 300 meters, making it ideal for festival environments. Here's a breakdown of the key components:
      </p>
      <!-- Sample Arduino Code -->


      <div v-highlight>
        <pre class="language-javascript">
        <code>
          # Sample Arduino code for sensor integration
          sensor1 = digitalRead(SENSOR1_PIN)
          sensor2 = digitalRead(SENSOR2_PIN)
        
          if sensor1 == HIGH and sensor2 == LOW:
              # Add a point to Team Red
              red_score += 1
              sendScoreToServer("red", red_score)
            </code>
        </pre>
      </div>


      <!-- Sensors -->
      <p>
        <strong>Sensors:</strong> We tested various sensors, including the Sharp GP2Y0A41SK0F Distance Sensor, a laser diode with a sensor, and the TCTR5000 infrared sensor. The TCTR5000 infrared sensor, with its ability to detect objects passing through, emerged as the most suitable choice for our project.
      </p>
      <!-- Sample Code for Using TCTR5000 Infrared Sensor -->

      <div v-highlight>
        <pre class="language-javascript">
        <code>
          # Sample code for using TCTR5000 infrared sensor
          if digitalRead(IR_SENSOR_PIN) == HIGH:
              # Object detected
              addPointToTeam("red")
        </code>
        </pre>
      </div>

      <!-- Server -->
      <p>
        <strong>Server:</strong> A server was a crucial component of the project, serving as the central hub for tracking and displaying team scores. ExpressJS, a JavaScript-based framework, was employed for the server. We selected this framework to facilitate communication with the ESP8266 units over Wi-Fi.
      </p>
      <!-- Sample ExpressJS Server Code -->

      <div v-highlight>
        <pre class="language-javascript">
        <code>
          // Sample ExpressJS server code for handling score updates
          app.post("/updateVals", (req, res) => {
              const team = req.body.team;
              const points = req.body.points;
              // Update the score and respond
              updateScore(team, points);
              res.status(200).json({ message: "Score updated successfully" });
          });
        </code>
        </pre>
      </div>

      <!-- Raspberry Pi -->
      <p>
        <strong>Raspberry Pi:</strong> We opted for a Raspberry Pi to manage the sound output, handle GPIO interactions, and make HTTP requests to the server. Sound was a key aspect of the project, and the Raspberry Pi provided the necessary capabilities for this.
      </p>
      <!-- Sample Python Code on Raspberry Pi for Playing Sound -->

      <div v-highlight>
        <pre class="language-javascript">
        <code>
          # Sample Python code on Raspberry Pi for playing sound
          import pygame.mixer as mixer
          mixer.init()
          sound = mixer.Sound('team_red.wav')
          sound.play()
        </code>
        </pre>
      </div>

    </section>
    <!-- Software Stack -->
    <section>
      <h2>Software Stack</h2>
      <p>
        To bring the project to life, we implemented a robust software stack that included:
      </p>
      <!-- Python for Raspberry Pi -->
      <h3>Python for Raspberry Pi</h3>
      <p>
        We utilized Python to manage GPIO pins, control sound, and execute HTTP requests. Various libraries such as Pygame, pysoundfile, and pyttsx3 were employed for handling sound, text-to-speech conversion, and asynchronous tasks.
      </p>
      <!-- ExpressJS Server -->
      <h3>ExpressJS Server</h3>
      <p>
        The server was built using ExpressJS and featured PUG as the templating engine. It provided real-time score updates, facilitated question and answer updates, and allowed the tracking of results.
      </p>
      <!-- Unit Tests -->
      <h3>Unit Tests</h3>
      <p>
        To ensure the reliability of the ExpressJS server, we implemented a suite of unit tests using Mocha, Chai, and Supertest. These tests covered various server functionalities, including page rendering and data retrieval.
      </p>
      <!-- Sample JavaScript Code for Enhanced Security Measures -->
      <div v-highlight>
        <pre class="language-javascript">
        <code>
          // Sample JavaScript code for enhanced security measures
          app.post("/newQuestion", (req, res) => {
              const password = req.body.password;
              if (password === "your_secure_password") {
                  // Handle question update
                  updateQuestion(req.body.question);
                  updateAnswers(req.body.answers);
                  res.status(200).json({ message: "Question updated successfully" });
              } else {
                  res.status(403).json({ message: "Wrong password" });
              }
          });
        </code>
        </pre>
      </div>
    </section>
    <!-- Outcome and Lessons Learned -->
    <section>
      <h2>Outcome and Lessons Learned</h2>
      <p>
        The "Paaspop Red & Blue Trash Concept" was successfully deployed at Paaspop, initially garnering positive results. Festival attendees actively engaged with the project, contributing to cleaner festival grounds and heightened enjoyment. However, the project encountered some challenges, most notably the theft of the large buttons on one occasion. This unfortunate incident underscored the need to enhance the project's durability and security measures in festival environments, considering the often exuberant behavior of festivalgoers.
      </p>
      <p>
        Overall, this project exemplifies our ability to conceptualize, design, and implement interactive solutions that tackle real-world challenges in creative and engaging ways. It also underscores our commitment to continuous improvement, as we draw valuable lessons from every project to ensure better outcomes in future endeavors.
      </p>
    </section>

    <br>
      <div class="columns is-multiline is-mobile">
        <div class="column is-one-third is-vcentered">
            <img src="../../assets/projects/pp19/image003.gif" alt="pp19">
        </div>
        <div class="column is-one-third is-vcentered">
            <img src="../../assets/projects/pp19/image029.png" alt="pp19">
        </div>
        <div class="column is-one-third is-vcentered">
            <img src="../../assets/projects/pp19/image008.png" alt="pp19">
        </div>
        <div class="column is-full is-vcentered">
            <img src="../../assets/projects/pp19/image020.png" alt="pp19">
        </div>
        <div class="column is-half is-vcentered">
            <img src="../../assets/projects/pp19/image022.png" alt="pp19">
        </div>
        <div class="column is-half is-vcentered">
            <img src="../../assets/projects/pp19/image024.png" alt="pp19">
        </div>
      </div> 
    <br>

  </div>

 
      
</template>


<style scoped>
  
@import 'project.css';

</style>